import * as z from "zod";

const CustomAnalyticsReportColumnKindSchema = z.enum([
  "text",
  "number",
  "integer",
  "currency",
  "percent",
  "date",
  "datetime",
  "boolean",
  "json",
]);

const CustomAnalyticsReportColumnFormatSchema = z.object({
  decimalPlaces: z.number().int().min(0).max(6).nullable(),
});

/**
 * Wire/persistence contract for inline custom analytics charts in Ask Redo.
 *
 * The generate-custom-analytics-chart tool embeds this payload in its tool
 * output, which is persisted verbatim on the LlmThread and re-rendered from
 * history. Bump the version only on breaking shape changes; the client parser
 * returns null on mismatch so old messages degrade to the generic tool label
 * instead of rendering a wrong chart.
 */
export const CUSTOM_ANALYTICS_CHART_VERSION = 1;

export const CUSTOM_ANALYTICS_CHART_MAX_PIE_SLICES = 12;
export const CUSTOM_ANALYTICS_CHART_MAX_BAR_CATEGORIES = 25;
export const CUSTOM_ANALYTICS_CHART_MAX_LINE_POINTS = 120;
export const CUSTOM_ANALYTICS_CHART_MAX_SERIES = 6;

export const CUSTOM_ANALYTICS_CHART_OTHER_LABEL = "Other";

export const CustomAnalyticsChartTypeSchema = z.enum(["pie", "line", "bar"]);

export type CustomAnalyticsChartType = z.infer<
  typeof CustomAnalyticsChartTypeSchema
>;

export const CustomAnalyticsChartTimeGrainSchema = z.enum([
  "day",
  "week",
  "month",
  "quarter",
  "year",
]);

export type CustomAnalyticsChartTimeGrain = z.infer<
  typeof CustomAnalyticsChartTimeGrainSchema
>;

/**
 * Value-axis formatting reuses the report column vocabulary so the frontend
 * can feed it straight into the existing custom report value formatter.
 */
export const CustomAnalyticsChartValueFormatSchema = z.object({
  kind: CustomAnalyticsReportColumnKindSchema,
  format: CustomAnalyticsReportColumnFormatSchema.nullable(),
});

export type CustomAnalyticsChartValueFormat = z.infer<
  typeof CustomAnalyticsChartValueFormatSchema
>;

export const CustomAnalyticsChartSeriesSchema = z.object({
  label: z.string(),
  /** Index-aligned with the chart `labels` array. */
  data: z.array(z.number().nullable()),
});

export type CustomAnalyticsChartSeries = z.infer<
  typeof CustomAnalyticsChartSeriesSchema
>;

export const CustomAnalyticsChartDimensionSchema = z.object({
  label: z.string(),
  kind: z.enum(["category", "time"]),
  timeGrain: CustomAnalyticsChartTimeGrainSchema.nullable(),
});

export type CustomAnalyticsChartDimension = z.infer<
  typeof CustomAnalyticsChartDimensionSchema
>;

export const CustomAnalyticsChartTruncationSchema = z.object({
  droppedRowCount: z.number().int().nonnegative(),
  hasOtherBucket: z.boolean(),
});

export type CustomAnalyticsChartTruncation = z.infer<
  typeof CustomAnalyticsChartTruncationSchema
>;

export const CustomAnalyticsChartSchema = z.object({
  version: z.literal(CUSTOM_ANALYTICS_CHART_VERSION),
  chartType: CustomAnalyticsChartTypeSchema,
  title: z.string(),
  /** Human period label rendered as "Title (Date range)". Null when unknown. */
  dateRangeLabel: z.string().nullable(),
  /** x-axis (line/bar) or slice dimension (pie). */
  dimension: CustomAnalyticsChartDimensionSchema,
  valueFormat: CustomAnalyticsChartValueFormatSchema,
  /** Pre-formatted x-axis labels / slice labels, in render order. */
  labels: z.array(z.string()),
  series: z.array(CustomAnalyticsChartSeriesSchema),
  truncation: CustomAnalyticsChartTruncationSchema.nullable(),
});

export type CustomAnalyticsChart = z.infer<typeof CustomAnalyticsChartSchema>;

export const CustomAnalyticsChartReadyOutputSchema = z.object({
  status: z.literal("chart_ready"),
  chart: CustomAnalyticsChartSchema,
});

export function tryParseCustomAnalyticsChartOutput(
  output: unknown,
): CustomAnalyticsChart | null {
  const result = CustomAnalyticsChartReadyOutputSchema.safeParse(output);
  return result.success ? result.data.chart : null;
}
